import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Check } from 'lucide-react';

import Popover from 'components/Popover';

const Checkbox = ({
  label,
  checked = false,
  onChange,
  error,
  required = false,
  className = '',
  popoverLinks = [],
  alignment = 'start',
  checkboxClasses = '',
  ...props
}) => {
  const [activePopover, setActivePopover] = useState(null);
  const checkboxRef = useRef(null);

  const handleLinkClick = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    setActivePopover(activePopover === index ? null : index);
  };

  const renderLabelWithLinks = () => {
    if (!popoverLinks.length) {
      return <span>{label}</span>;
    }

    const processedLabel = label;
    const elements = [];
    let lastIndex = 0;

    popoverLinks.forEach((link, index) => {
      const linkIndex = processedLabel.indexOf(link.text, lastIndex);
      if (linkIndex !== -1) {
        // Add text before the link
        if (linkIndex > lastIndex) {
          elements.push(processedLabel.substring(lastIndex, linkIndex));
        }

        // Add the link
        elements.push(
          <a
            key={index}
            href="#"
            className="text-blue-500 transition hover:text-blue-700 underline cursor-pointer"
            onClick={(e) => handleLinkClick(e, index)}
          >
            {link.text}
          </a>
        );

        lastIndex = linkIndex + link.text.length;
      }
    });

    // Add remaining text
    if (lastIndex < processedLabel.length) {
      elements.push(processedLabel.substring(lastIndex));
    }

    return elements.map((element, index) => (
      <React.Fragment key={index}>{element}</React.Fragment>
    ));
  };

  const activeLink = activePopover !== null ? popoverLinks[activePopover] : null;

  return (
    <div className={`relative flex flex-col gap-2 ${className}`}>
      <div className={`flex items-${alignment} gap-3`}>
        <div ref={checkboxRef} className={`relative flex-shrink-0 mt-0.5 ${checkboxClasses}`}>
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            className="sr-only peer"
            {...props}
          />
          <div
            className={`
              w-5 h-5 border-2 rounded cursor-pointer transition-all duration-200 flex items-center justify-center
              peer-focus:ring-2 peer-focus:ring-blue-400 peer-focus:ring-offset-1
              ${checked
              ? 'bg-blue-500 border-blue-500'
              : error
                ? 'border-red-300 bg-white hover:border-red-400'
                : 'border-gray-300 bg-white hover:border-gray-400'
              }
            `}
            onClick={() => onChange(!checked)}
          >
            {checked && <Check size={14} className="text-white" />}
          </div>
        </div>

        <div className="flex-1">
          <div className="text-sm transition-colors duration-200 text-gray-500">
            {renderLabelWithLinks()}
            {required && <span className="text-red-500 ml-1">*</span>}
          </div>
        </div>
      </div>

      {error && <p className="text-xs text-red-500 pl-8 pr-2">{error}</p>}

      {activeLink && (
        <Popover
          isOpen
          onClose={() => setActivePopover(null)}
          title={activeLink.title}
          targetRef={checkboxRef}
          placement="top"
        >
          <div className="text-sm text-gray-700">
            {activeLink.content}
          </div>
        </Popover>
      )}
    </div>
  );
};

Checkbox.propTypes = {
  label: PropTypes.string.isRequired,
  checked: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  required: PropTypes.bool,
  className: PropTypes.string,
  popoverLinks: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      content: PropTypes.string.isRequired,
    })
  ),
  alignment: PropTypes.oneOf(['start', 'center', 'end']),
  checkboxClasses: PropTypes.string,
};

Checkbox.defaultProps = {
  checked: false,
  error: '',
  required: false,
  className: '',
  popoverLinks: [],
  alignment: 'start',
  checkboxClasses: '',
};

export default Checkbox;
